import { create } from 'zustand'

import {
  getAllSessions,
  getSession,
  createSession as pbCreateSession,
  updateSession as pbUpdateSession,
  deleteSession as pbDeleteSession,
  closeSession as pbCloseSession,
  openCheckinSession,
} from '../api/sessionsService'

import type { ClassSession, CloseSessionResult, OpenCheckinPayload, SessionStatus } from './types'

function generateId(): string {
  return crypto.randomUUID()
}

/** Type guard for API errors with a numeric status field. */
function isApiError(err: unknown): err is { status: number; message: string } {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    typeof (err as Record<string, unknown>).status === 'number'
  )
}

interface SessionState {
  sessions: ClassSession[]
  isLoading: boolean
  sessionsLoaded: boolean
  error: string | null

  loadSessions: () => Promise<void>
  createSession: (
    data: Pick<ClassSession, 'courseId' | 'location' | 'date' | 'instructorName' | 'maxAttendees'> & { startTime?: string; endTime?: string }
  ) => Promise<ClassSession>
  openCheckin: (sessionId: string, payload?: OpenCheckinPayload) => Promise<void>
  beginClass: (sessionId: string) => Promise<void>
  closeSession: (sessionId: string, attendeeIds?: string[]) => Promise<CloseSessionResult>
  updateSessionMaxAttendees: (sessionId: string, maxAttendees: number) => Promise<void>
  updateSession: (
    sessionId: string,
    data: Partial<Pick<ClassSession, 'courseId' | 'location' | 'date' | 'status' | 'instructorName' | 'instructorId' | 'maxAttendees' | 'language' | 'startedAt' | 'closedAt'>> &
      Partial<{ startTime: string; endTime: string; locationId: string }>
  ) => Promise<unknown>
  deleteSession: (sessionId: string) => Promise<void>
}

export const useSessionStore = create<SessionState>((set) => ({
  sessions: [],
  isLoading: false,
  sessionsLoaded: false,
  error: null,

  loadSessions: async () => {
    set({ isLoading: true, error: null })

    async function tryLoad(): Promise<ClassSession[]> {
      const pbSessions = await getAllSessions()
      return pbSessions
        .map((s) => ({
          id: s.id,
          qrToken: s.qrToken,
          courseId: s.courseId,
          courseName: s.courseName,
          courseSlug: s.courseSlug,
          location: s.location,
          locationName: s.locationName,
          locationId: s.locationId,
          date: s.date,
          startTime: s.startTime,
          endTime: s.endTime,
          durationMinutes: s.durationMinutes,
          status: s.status,
          instructorName: s.instructorName,
          maxAttendees: s.maxAttendees ?? 20,
          spotsAvailable: s.spotsAvailable ?? s.maxAttendees ?? 20,
          attendeeCount: s.attendeeCount,
          language: s.language ?? 'any',
          startedAt: s.startedAt ?? null,
          closedAt: s.closedAt ?? null,
          scheduleSource: s.scheduleSource,
        }))
        .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    }

    try {
      const sessions = await tryLoad()
      set({ sessions, isLoading: false, sessionsLoaded: true })
    } catch (err) {
      console.error('[SessionStore] loadSessions failed:', err)

      const isNetworkError = isApiError(err) && err.status === 0
      if (isNetworkError) {
        console.warn('[SessionStore] Retrying in 1s...')
        await new Promise((res) => setTimeout(res, 1000))
        try {
          const sessions = await tryLoad()
          set({ sessions, isLoading: false, sessionsLoaded: true })
          return
        } catch (retryErr) {
          console.error('[SessionStore] Retry also failed:', retryErr)
        }
      }

      let message = 'Error al cargar sesiones'
      if (isApiError(err)) {
        const { status } = err
        const raw = err.message
        if (status === 0) {
          message = 'Sin conexión. Verifica que el servidor esté en línea y la URL sea correcta.'
        } else if (status === 401 || status === 403) {
          message = `Sin autorización (código ${status}). Por favor cierra sesión e inicia sesión de nuevo.`
        } else if (status === 404) {
          message = 'Recurso no encontrado (404). Verifica que el endpoint exista en el backend.'
        } else {
          message = `API error ${status}: ${raw}`
        }
      } else if (err instanceof Error) {
        message = err.message
      }
      set({ error: message, isLoading: false })
    }
  },

  createSession: async (data) => {
    const qrToken = generateId()
    const pbSession = await pbCreateSession({
      qrToken,
      courseId: data.courseId,
      location: data.location,
      date: data.date,
      startTime: data.startTime,
      endTime: data.endTime,
      status: 'draft' as SessionStatus,
      instructorName: data.instructorName,
      maxAttendees: data.maxAttendees ?? 20,
      startedAt: null,
      closedAt: null,
    })
    const session: ClassSession = {
      id: pbSession.id,
      qrToken: pbSession.qrToken,
      courseId: pbSession.courseId,
      location: pbSession.location,
      locationName: pbSession.locationName,
      locationId: pbSession.locationId,
      date: pbSession.date,
      startTime: pbSession.startTime,
      endTime: pbSession.endTime,
      durationMinutes: pbSession.durationMinutes,
      status: pbSession.status,
      instructorName: pbSession.instructorName,
      maxAttendees: pbSession.maxAttendees ?? 20,
      language: pbSession.language ?? 'any',
      startedAt: pbSession.startedAt ?? null,
      closedAt: pbSession.closedAt ?? null,
      scheduleSource: pbSession.scheduleSource,
    }
    set((s) => {
      if (s.sessions.some((sess) => sess.id === session.id)) {return s}
      return { sessions: [session, ...s.sessions] }
    })
    return session
  },

  openCheckin: async (sessionId, payload) => {
    await openCheckinSession(sessionId, payload)
    const updated = await getSession(sessionId)
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === sessionId
          ? {
              ...sess,
              status: 'checking' as SessionStatus,
              location: updated.locationName ?? updated.location ?? sess.location,
              locationName: updated.locationName ?? sess.locationName,
              locationId: updated.locationId ?? sess.locationId,
              spotsAvailable: updated.spotsAvailable ?? sess.spotsAvailable,
            }
          : sess
      ),
    }))
  },

  beginClass: async (sessionId) => {
    const now = new Date().toISOString()
    await pbUpdateSession(sessionId, { status: 'active', startedAt: now })
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === sessionId
          ? { ...sess, status: 'active' as SessionStatus, startedAt: now }
          : sess
      ),
    }))
  },

  closeSession: async (sessionId, attendeeIds) => {
    const now = new Date().toISOString()
    const result = await pbCloseSession(sessionId, attendeeIds)
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === sessionId
          ? { ...sess, status: 'closed' as SessionStatus, closedAt: now }
          : sess
      ),
    }))
    return result
  },

  updateSessionMaxAttendees: async (sessionId, maxAttendees) => {
    await pbUpdateSession(sessionId, { maxAttendees })
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === sessionId ? { ...sess, maxAttendees } : sess
      ),
    }))
  },

  updateSession: async (sessionId, data) => {
    const updated = await pbUpdateSession(sessionId, data)
    set((s) => ({
      sessions: s.sessions.map((sess) =>
        sess.id === sessionId
          ? {
              ...sess,
              ...(data.courseId !== undefined && { courseId: data.courseId }),
              ...(data.location !== undefined && {
                location: data.location,
                locationName: data.location,
              }),
              ...(data.locationId !== undefined && {
                location: updated.location,
                locationName: updated.locationName ?? updated.location,
                locationId: updated.locationId,
              }),
              ...(data.date !== undefined && { date: data.date }),
              ...(data.startTime !== undefined && { startTime: data.startTime }),
              ...(data.endTime !== undefined && { endTime: data.endTime }),
              ...(data.status !== undefined && { status: data.status }),
              ...(data.instructorName !== undefined && { instructorName: data.instructorName }),
              ...(data.instructorId !== undefined && {
                instructorId: data.instructorId,
                instructorName: updated.instructorName ?? sess.instructorName,
              }),
              ...(data.maxAttendees !== undefined && { maxAttendees: data.maxAttendees }),
              ...(data.language !== undefined && { language: data.language }),
              ...(data.startedAt !== undefined && { startedAt: data.startedAt }),
              ...(data.closedAt !== undefined && { closedAt: data.closedAt }),
              scheduleSource: updated.scheduleSource ?? sess.scheduleSource,
            }
          : sess
      ),
    }))
    return updated
  },

  deleteSession: async (sessionId) => {
    await pbDeleteSession(sessionId)
    set((s) => ({
      sessions: s.sessions.filter((sess) => sess.id !== sessionId),
    }))
  },
}))
